import { ResourceClient } from '../../../api/resource';
import { companiesApi, lookupApis, pocApis } from '../../admin/admin.api';
import { DEFAULT_STATE, INDIAN_STATE_OPTIONS } from '../../shared/constants/indianStates';

export interface MasterFieldDef {
  /** Request key, snake_case, matching the controller's body. */
  name: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'textarea' | 'number' | 'boolean' | 'autocomplete';
  /** `type: 'autocomplete'` only: a type-to-search list; only listed values are kept. */
  options?: Array<{ label: string; value: string }>;
  helperText?: string;
  defaultValue?: unknown;
}

export interface MasterConfig {
  /** Singular noun for the dialog title and toasts, e.g. "Locality". */
  entityName: string;
  client: ResourceClient<any>;
  fields: MasterFieldDef[];
  /** Where the option label lives on an API row — lookups use `label`, contacts use `name`. */
  labelKey: 'label' | 'name';
}

/**
 * The masters the job edit screen can add to and edit in place.
 *
 * Each form carries the same fields as that master's own admin screen — the three contact
 * types match their Contact Masters tab, the lookups match their Reference Data tab — so
 * a row created mid-job-edit is as complete as one created from the master screen, and
 * nothing has to be filled in twice.
 *
 * Status is absent on purpose. Status rows carry a category and drive workflow logic — the
 * API special-cases the "Canceled" label to also store a cancellation reason — so a status
 * invented here would be accepted by the form and then ignored by the code behind it.
 */

/** mobile/phone/whatsapp/email — the contact block all four POC tables share. */
const contactFields: MasterFieldDef[] = [
  { name: 'mobile', label: 'Mobile' },
  { name: 'phone', label: 'Phone' },
  { name: 'whatsapp', label: 'WhatsApp' },
  { name: 'email', label: 'Email' },
];

const nameField: MasterFieldDef = { name: 'name', label: 'Name', required: true };

/**
 * V36 — what a contact needs when it is a job's client: there is no separate clients master
 * any more, so the owner, architect and builder carry these. `state` decides CGST + SGST
 * against IGST on its bills; left blank, the GST number's first two digits decide.
 */
const billingFields: MasterFieldDef[] = [
  { name: 'gst_number', label: 'GST Number', helperText: '15 characters, e.g. 27ABCDE1234F1Z5' },
  {
    name: 'state',
    label: 'State',
    type: 'autocomplete',
    options: INDIAN_STATE_OPTIONS,
    defaultValue: DEFAULT_STATE,
    helperText: 'Outside the company\'s state, its bills charge IGST',
  },
  { name: 'short_code', label: 'Short Code', helperText: 'On quotation REF lines and bill numbers, e.g. MOTE' },
];

/**
 * code/label/description/sort/active — the shape the four job lookups share. `code` is
 * optional: left blank, the controller derives it from the label.
 */
const lookupFields = (labelText: string): MasterFieldDef[] => [
  { name: 'label', label: labelText, required: true },
  { name: 'code', label: 'Code', helperText: 'Leave blank to derive it from the name' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
];

export const JOB_MASTERS = {
  /**
   * The firm's own companies (V19). Same fields as the Company Master screen. Writing
   * needs ADMIN on the API, so screens offer "+ Add" / edit for it to admins only.
   */
  companies: {
    entityName: 'Company',
    client: companiesApi,
    labelKey: 'name',
    fields: [
      nameField,
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'contact_no1', label: 'Telephone No.' },
      { name: 'contact_no2', label: 'WhatsApp No.' },
      { name: 'email', label: 'Email' },
      { name: 'email2', label: 'Email 2' },
      { name: 'website', label: 'Website' },
      { name: 'gst_no', label: 'GST No.', helperText: '15 characters, e.g. 27ABCDE1234F1Z5' },
      { name: 'registration_no', label: 'Registration No.' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State', type: 'autocomplete', options: INDIAN_STATE_OPTIONS, defaultValue: DEFAULT_STATE },
      { name: 'country', label: 'Country', defaultValue: 'India' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  localities: {
    entityName: 'Locality',
    client: lookupApis.localities,
    labelKey: 'label',
    fields: lookupFields('Locality Name'),
  },
  projectTypes: {
    entityName: 'Project Type',
    client: lookupApis.projectTypes,
    labelKey: 'label',
    fields: lookupFields('Project Type'),
  },
  constructionWorkTypes: {
    entityName: 'Construction Work Type',
    client: lookupApis.constructionWorkTypes,
    labelKey: 'label',
    fields: lookupFields('Construction Work Type'),
  },
  buildingTypes: {
    entityName: 'Building Type',
    client: lookupApis.buildingTypes,
    labelKey: 'label',
    fields: lookupFields('Building Type'),
  },
  architects: {
    entityName: 'Architect',
    client: pocApis.architects,
    labelKey: 'name',
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }, ...billingFields],
  },
  builders: {
    entityName: 'Builder',
    client: pocApis.builders,
    labelKey: 'name',
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }, ...billingFields],
  },
  owners: {
    entityName: 'Owner',
    client: pocApis.owners,
    labelKey: 'name',
    fields: [
      nameField,
      { name: 'building_name', label: 'Building Name' },
      ...contactFields,
      { name: 'office_address', label: 'Office Address', type: 'textarea' },
      ...billingFields,
    ],
  },
} as const satisfies Record<string, MasterConfig>;

export type JobMasterKey = keyof typeof JOB_MASTERS;
